// AuthContext.js
// Centralised auth + consent + login tracking using Supabase services

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';
import { auth, database } from '../services/supabase';

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
};

export const AuthProvider = ({ children }) => {
  // Authentication state
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  // Consent state
  const [hasConsent, setHasConsent] = useState(false);
  const [consentLoading, setConsentLoading] = useState(false);

  useEffect(() => {
    let mounted = true;

    // Helper: check consent for the canonical Supabase Auth user UUID.
    const checkConsent = async (userId) => {
      if (!userId || !mounted) return;

      setConsentLoading(true);
      try {
        const result = await database.getUserConsent(userId);

        if (!mounted) return;

        if (result?.success) {
          setHasConsent(!!result.hasConsent);
        } else {
          setHasConsent(false);
        }
      } catch {
        if (mounted) {
          setHasConsent(false);
        }
      } finally {
        if (mounted) {
          setConsentLoading(false);
        }
      }
    };

    /**
     * Helper: handle user sign-in side effects
     * - upsert profile
     * - optionally record login (increment login_count, update last_login_at)
     * - refresh consent state
     */
    const handleSignIn = async (signedInUser, { recordLogin = false } = {}) => {
      if (!signedInUser || !mounted) return;

      try {
        // 1) Profile upsert
        await database.upsertUserProfile(signedInUser);

        // 2) Login tracking (only when explicitly asked)
        if (recordLogin) {
          await database.recordLogin(signedInUser.id, signedInUser.email);
        }

        // 3) Consent state
        await checkConsent(signedInUser.id);
      } catch {
        // Intentionally swallow errors here — we don't want auth to break
      }
    };

    // Initial session load on app mount
    const initializeAuth = async () => {
      try {
        const {
          data: { session },
          error,
        } = await auth.getCurrentSession();

        if (!mounted) return;

        if (error) {
          setUser(null);
          setSession(null);
          setHasConsent(false);
          setLoading(false);
          return;
        }

        setSession(session ?? null);
        const currentUser = session?.user ?? null;
        setUser(currentUser);

        if (currentUser) {
          // Initial hydration of state should NOT count as a new login
          await handleSignIn(currentUser, { recordLogin: false });
        }

        setLoading(false);
      } catch {
        if (mounted) {
          setUser(null);
          setSession(null);
          setHasConsent(false);
          setLoading(false);
        }
      }
    };

    initializeAuth();

    // Subscribe to auth state changes
    let subscription = null;
    try {
      const {
        data: { subscription: sub },
      } = auth.onAuthStateChange(async (event, newSession) => {
        if (!mounted) return;

        setSession(newSession ?? null);
        const newUser = newSession?.user ?? null;
        setUser(newUser);

        if (newUser) {
          // Only count actual sign-in events as "logins"
          // e.g. event === 'SIGNED_IN'
          await handleSignIn(newUser, {
            recordLogin: event === 'SIGNED_IN',
          });
        } else {
          // Signed out or no session
          setHasConsent(false);
        }

        setLoading(false);
      });

      subscription = sub;
    } catch {
      if (mounted) {
        setUser(null);
        setSession(null);
        setHasConsent(false);
        setLoading(false);
      }
    }

    // Cleanup on unmount
    return () => {
      mounted = false;
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, []);

  // Auth methods

  const signInWithGoogle = async () => {
    setLoading(true);
    try {
      const result = await auth.signInWithGoogle();

      if (!result?.success) {
        setLoading(false);
        return { success: false, error: result?.error || 'Sign-in failed' };
      }

      // For OAuth, Supabase will redirect away; we rely on the auth
      // state listener after redirect to set user + record login.
      return { success: true };
    } catch {
      setLoading(false);
      return { success: false, error: 'An unexpected error occurred' };
    }
  };

  const signOut = async () => {
    try {
      const result = await auth.signOut();

      if (result?.success) {
        setUser(null);
        setSession(null);
        setHasConsent(false);
      }

      return result;
    } catch {
      return { success: false, error: 'Failed to sign out' };
    }
  };

  // Consent methods

  const giveConsent = async () => {
    if (!user) {
      return { success: false, error: 'User not authenticated' };
    }

    setConsentLoading(true);
    try {
      const result = await database.recordConsent(user.email);

      if (result?.success) {
        setHasConsent(true);
        return { success: true };
      }
      return { success: false, error: result?.error || 'Failed to record consent' };
    } catch {
      return { success: false, error: 'Failed to record consent' };
    } finally {
      setConsentLoading(false);
    }
  };

  const withdrawConsent = async () => {
    if (!user) {
      return { success: false, error: 'User not authenticated' };
    }

    setConsentLoading(true);
    try {
      const result = await database.withdrawConsent(user.email);

      if (result?.success) {
        setHasConsent(false);
        return { success: true };
      }
      return { success: false, error: result?.error || 'Failed to withdraw consent' };
    } catch {
      return { success: false, error: 'Failed to withdraw consent' };
    } finally {
      setConsentLoading(false);
    }
  };

  const checkUserConsent = async () => {
    if (!user?.id) return;

    setConsentLoading(true);
    try {
      const result = await database.getUserConsent(user.id);

      if (result?.success) {
        setHasConsent(!!result.hasConsent);
      } else {
        setHasConsent(false);
      }
    } catch {
      setHasConsent(false);
    } finally {
      setConsentLoading(false);
    }
  };

  // Derived booleans
  const isAuthenticated = !!user;
  const canAccessModules = isAuthenticated && hasConsent;

  const value = {
    // Auth state
    user,
    session,
    isAuthenticated,
    loading,

    // Consent state
    hasConsent,
    consentLoading,
    canAccessModules,

    // Auth methods
    signInWithGoogle,
    signOut,

    // Consent methods
    giveConsent,
    withdrawConsent,
    checkUserConsent,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
